import { useEffect, useState } from "react";
import { Table as BTable, Button, Spinner } from "react-bootstrap";
import { taskTypeOptions, taskTypes } from "@/constants/taskTypes";
import Tabs from "@/components/Elements/tabs";
import {
  flexRender,
  getCoreRowModel,
  useReactTable,
} from "@tanstack/react-table";
import { TableCell } from "@/components/Elements/TableCell";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { authApi, matchApi } from "@/api";
import { toast } from "react-toastify";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { getPathForStage } from "@/util/functions";

const Labeler = () => {
  const { user } = useSelector(({ auth }) => auth);
  const [taskType, setTaskType] = useState("all");
  const [tasks, setTasks] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [matches, setMatches] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [pageNum, setPageNum] = useState(1);
  const [loadingMore, setLoadingMore] = useState(false);

  const navigate = useNavigate();
  async function redirectTo(stage, matchId) {
    navigate(await getPathForStage(stage, matchId));
  }

  const checkPermission = async (id) => {
    try {
      const permissions = await authApi.getPermissions(id);
      const permissionList = [
        "can_solve_track",
        "can_solve_super_track",
      ];
      if (!permissions) navigate("/panel/intern");
      const isIntern = permissionList.every(
        (type) => permissions[type] === false
      );
      if (isIntern) navigate("/panel/intern");
    } catch (err) {
      if (err.response && err.response.status === 404) {
        // Handle 404 error
        navigate("/panel/intern");
      } else {
        // Handle other errors
        toast.error("Something went wrong");
        console.error("Error:", err.message);
      }
    }
  };

  const columns = [
    { accessorKey: "id", header: "NO" },
    {
      accessorKey: "name",
      header: "Name",
    },
    {
      accessorKey: "progress",
      header: "Progress",
    },
    {
      accessorKey: "stage",
      header: "Stage",
    },
    {
      header: "Action",
      cell: (info) => (
        <Button
          className="tw-px-3"
          variant="secondary"
          title="Go to Task"
          onClick={() => {
            const { id, stage } = info.row.original;
            console.log({ stage, taskType });
            redirectTo(stage, id);
          }}
          size="sm"
        >
          <FontAwesomeIcon icon={["fas", "hand-point-right"]} size="sm" />
        </Button>
      ),
    },
  ];

  const table = useReactTable({
    data: tasks[taskType] || [],
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  const fetchMoreMatches = async () => {
    try {
      setPageNum(pageNum + 1);
      const res = await matchApi.getMatchesList(pageNum + 1);
      setMatches([...matches, ...res.results]);
    } catch (error) {
      console.error("Error fetching data:", error);
      toast.error("Something went wrong while fetching data.");
    }
  };

  useEffect(() => {
    if (!user) return;

    async function prepareData() {
      try {
        const { id, is_staff } = user;

        // if he is admin we skip this permsision check
        if (!is_staff) {
          checkPermission(id);
        }

        setIsLoading(true);

        const res = await matchApi.getMatchesList();
        setMatches(res.results);
        setTotalCount(res.count);
      } catch (err) {
        console.error("There was an error while fetching data", err);
        toast.error("Something went wrong while fetching data.");
      } finally {
        setIsLoading(false);
      }
    }
    prepareData();
  }, [user]);

  useEffect(() => {
    const stages = {
      BALL_SUPER_TRACK: "ball_supertrack",
      SUPER_TRACKS: "supertrack",
      EVENT_VALIDATION: "validate",
      MATCH_EVENT: "match_event",
      EVENT_TAGGING: "event_tagging",
    };
    let tasks = {};
    for (const match of matches) {
      const {
        id,
        name,
        pipe_conflicts: { full_count, blocked_count },
        task: { stage },
      } = match;
      const taskType = stages[stage];
      if (!taskType) continue;
      if (!tasks[taskType]) tasks[taskType] = [];

      // if the task type is validate, we need to add the new labeling task
      // if (taskType === "validate") {
      //   if (!tasks["new_labeling"]) tasks["new_labeling"] = [];
      //   tasks["new_labeling"] = [
      //     ...tasks["new_labeling"],
      //     {
      //       id,
      //       name,
      //       stage,
      //       progress: blocked_count + "/" + full_count,
      //     },
      //   ];
      // }

      tasks[taskType] = [
        ...tasks[taskType],
        {
          id,
          name,
          stage,
          progress: blocked_count + "/" + full_count,
        },
      ];
    }
    tasks["all"] = [];
    for (const type of taskTypes) {
      if (!tasks[type]) continue;
      tasks["all"] = [...tasks["all"], ...tasks[type]];
    }
    setTasks({ ...tasks });
  }, [matches]);

  const handleLoadMore = async () => {
    setLoadingMore(true);
    await fetchMoreMatches();
    setLoadingMore(false);
  };

  return (
    <div className="tw-py-3 sm:tw-py-6 tw-h-full tw-flex tw-flex-col">
      <h2 className="tw-text-center tw-font-medium tw-tracking-wider">
        Labeler
      </h2>
      <div className="tw-w-full tw-flex tw-justify-center tw-my-3">
        <div className="tw-rounded-full tw-bg-white tw-w-fit tw-px-2 tw-py-1 tw-shadow-sm">
          <Tabs
            tabs={taskTypeOptions}
            onChange={(index) => {
              setTaskType(taskTypeOptions[index].value);
            }}
          />
        </div>
      </div>
      <div className="tw-flex tw-justify-center px-2 tw-flex-1 tw-overflow-y-auto tw-overflow-x-hidden">
        {tasks[taskType]?.length ? (
          <div className="tw-px-2 sm:tw-px-5 tw-bg-white tw-rounded-lg tw-w-full sm:tw-w-fit tw-h-full tw-shadow-sm tw-overflow-y-auto tw-overflow-x-hidden">
            <BTable className="tw-w-full sm:tw-w-[600px]" hover size="md">
              <thead className="tw-py-1 sm:tw-py-2">
                {table.getHeaderGroups().map((headerGroup) => (
                  <tr key={headerGroup.id}>
                    {headerGroup.headers.map((header) => (
                      <th key={header.id} colSpan={header.colSpan}>
                        {header.isPlaceholder
                          ? null
                          : flexRender(
                              header.column.columnDef.header,
                              header.getContext()
                            )}
                      </th>
                    ))}
                  </tr>
                ))}
              </thead>
              <tbody>
                {table.getRowModel().rows.map((row) => (
                  <tr key={row.id}>
                    {row.getVisibleCells().map((cell) => (
                      <TableCell key={cell.id}>
                        {flexRender(
                          cell.column.columnDef.cell,
                          cell.getContext()
                        )}
                      </TableCell>
                    ))}
                  </tr>
                ))}
              </tbody>
            </BTable>
          </div>
        ) : (
          <div className="tw-m-3 tw-flex tw-justify-center">
            {isLoading ? (
              <Spinner animation="border" role="status" size="sm">
                <span className="visually-hidden">Loading...</span>
              </Spinner>
            ) : (
              "There is no task."
            )}
          </div>
        )}
      </div>
      {matches.length < totalCount ? (
        <Button
          className="tw-w-fit tw-px-4 tw-my-2 tw-self-center disabled:!tw-cursor-not-allowed disabled:tw-bg-gray-500 tw-border-none"
          size="sm"
          onClick={handleLoadMore}
          disabled={loadingMore}
        >
          {!loadingMore ? (
            "Load more"
          ) : (
            <>
              Loading{" "}
              <Spinner animation="border" role="status" size="sm"></Spinner>
            </>
          )}
        </Button>
      ) : null}
    </div>
  );
};

export default Labeler;
